import { loadedPlugins } from "@/Plugins/LoadedPlugins";
import { matchesTag } from "@/Plugins/Core/ActivityFocus/ActivityFocusUtils";
import { Vue } from "vue-facing-decorator";
export var MenuItemType;
(function (MenuItemType) {
    MenuItemType[MenuItemType["Action"] = 0] = "Action";
    MenuItemType[MenuItemType["Submenu"] = 1] = "Submenu";
    MenuItemType[MenuItemType["Separator"] = 2] = "Separator";
})(MenuItemType || (MenuItemType = {}));
/**
 * MenuLevelParent component
 */
export class MenuLevelParent extends Vue {
    /**
     * Determines if a menu item is an action.
     *
     * @param {IMenuEntry | IMenuSeparator} item  The menu item to check.
     * @returns {boolean} True if the item is an action.
     */
    isAction(item) {
        return item.type === MenuItemType.Action;
    }
    /**
     * Determines if a menu item is a separator.
     *
     * @param {IMenuEntry | IMenuSeparator} item  The menu item to check.
     * @returns {boolean} True if the item is a separator.
     */
    isSeparator(item) {
        return item.type === MenuItemType.Separator;
    }
    /**
     * Gets the submenu items for a menu item. They are sorted.
     *
     * @param {IMenuEntry} item  The menu item.
     * @returns {IMenuEntry[]}  The submenu items.
     */
    getItems(item) {
        const { items } = item;
        menuDataSorted(items);
        return items;
    }
}
/**
 * Given a list of menu entries, does one of them have a given name?
 *
 * @param  {IMenuEntry[]} menuDat List of menu entries.
 * @param  {string}       name    Name to look for.
 * @returns {boolean} True if found, false otherwise.
 */
function _isNameInMenuData(menuDat, name) {
    return menuDat.map((m) => m.text).includes(name);
}
/**
 * Given menu data, get the submenu of an item with a given name.
 *
 * @param  {IMenuEntry[]} menuDat  List of menu entries.
 * @param  {string}       name     Name of submenu to get.
 * @returns {IMenuSubmenu} Submenu with the given name.
 */
function _getSubMenu(menuDat, name) {
    return menuDat.find((m) => m.text === name);
}
/**
 * Given any kind of menu path (string[], string, etc.), returns the menu path
 * info.
 *
 * @param  {string[]|string|null|undefined} menuPath  Menu path.
 * @returns {IMenuPathInfo}  Menu path info.
 */
export function processMenuPath(menuPath) {
    if (menuPath === null || menuPath === undefined) {
        return null;
    }
    if (typeof menuPath === "string") {
        menuPath = menuPath.split("/");
    }
    // If you get here, it's an array of strings.
    // Extract the rank, if any.
    return menuPath.map((m) => _extractRankFromText(m));
}
/**
 * Extract the rank in menu text, if any.
 *
 * @param  {string} text  Text to extract rank from.
 * @returns {IMenuPathInfo} Contains both the rank and the text with ranked removed.
 */
function _extractRankFromText(text) {
    const rankInfo = text.match(/^\[(\d+)\]/);
    let rank = undefined;
    if (rankInfo) {
        rank = parseInt(rankInfo[1]);
        text = text.replace(/^\[\d+\]/, "").trim();
        // If rank is not between 0 and 10, throw an error.
        if (rank < 0 || rank > 10) {
            throw new Error("Rank must be between 0 and 10. Found: " + rank);
        }
    }
    return { rank, text };
}
/**
 * Sets defaults on menu items, for cases when not specified in plugins. All
 * this in place, so no need to return anything.
 *
 * @param  {IMenuItem} newMenuItem  Menu item, with defaults set.
 */
function _setNewMenuItemDefaults(newMenuItem) {
    // If type is not set, default to MenuItemType.ACTION.
    if (newMenuItem.type === undefined) {
        newMenuItem.type = MenuItemType.Action;
    }
    // If rank is not set, default to 5.
    // if (newMenuItem._rank === undefined) {
    //     newMenuItem._rank = 5;
    // }
    // // If rank is not between 0 and 10, throw an error.
    // if (newMenuItem._rank < 0 || newMenuItem._rank > 10) {
    //     throw new Error("Rank must be between 0 and 10.");
    // }
}
/**
 * Add an entry to the menu system.
 *
 * @param  {IMenuItem} newMenuItem           Menu item to add.
 * @param  {IMenuEntry[]} existingMenuItems  List of existing menu items.
 * @param  {string} [pluginId]               Plugin id to add. Optional.
 * @returns {IMenuEntry[]}  The new menu, with the entry added.
 */
export function addMenuItem(newMenuItem, existingMenuItems, pluginId) {
    // if (api.sys.loadStatus.menuFinalized) {
    //     // Error: Menu already finalized. Assert
    //     throw new Error("Menu already finalized.");
    // }
    if (newMenuItem.path === null) {
        // One of the rare plugins that does't use the menu system.
        return existingMenuItems;
    }
    // If tags don't match, don't add the menu item.
    if (pluginId !== undefined) {
        const plugin = loadedPlugins[pluginId];
        const { tags } = plugin;
        if (!matchesTag(tags)) {
            return existingMenuItems;
        }
    }
    // Plugin is excluded per the plugin user parameter
    // if (
    //     pluginId !== undefined &&
    //     LoadedPlugins.alwaysEnabledPlugins.indexOf(pluginId) === -1
    // ) {
    //     return existingMenuItems;
    // }
    const menuPathInfo = processMenuPath(newMenuItem.path);
    const actionItem = menuPathInfo?.pop();
    // Separate path data to _text and _pathNames rather than path.
    newMenuItem.text = actionItem?.text;
    newMenuItem._pathNames = menuPathInfo?.map((m) => m.text);
    newMenuItem.path = undefined;
    // Add rank too
    newMenuItem._rank = actionItem?.rank;
    _setNewMenuItemDefaults(newMenuItem);
    let existingMenuItemsPlaceholder = existingMenuItems;
    for (let i = 0; i < menuPathInfo.length; i++) {
        // Get rank and pathName from text.
        const pathNameWithoutRank = menuPathInfo[i].text;
        const { rank } = menuPathInfo[i];
        if (!_isNameInMenuData(existingMenuItemsPlaceholder, pathNameWithoutRank)) {
            // This level doesn't exist, so add it.
            existingMenuItemsPlaceholder.push({
                type: MenuItemType.Submenu,
                text: pathNameWithoutRank,
                items: [],
                _rank: rank,
            });
        }
        const subMenu = _getSubMenu(existingMenuItemsPlaceholder, pathNameWithoutRank);
        // Check whether to update rank.
        if (rank !== undefined) {
            if (subMenu._rank === undefined) {
                subMenu._rank = rank;
            }
            else if (subMenu._rank !== rank) {
                // Error: Rank already set. Assert
                throw new Error(`Plugin "${pluginId}" set rank of "${subMenu.text}" menu item to ${rank}, but it is already set to ${subMenu._rank}.`);
            }
        }
        existingMenuItemsPlaceholder = subMenu.items;
    }
    // You've gone down all the levels in pathNames, so add the item.
    existingMenuItemsPlaceholder.push(newMenuItem);
    // Return the whole menu-data object.
    return existingMenuItems;
}
/**
 * Sort menu data by rank, then by text if rank equal. In place, so no need to
 * return anything.
 *
 * @param  {IMenuEntry[]} menuData  Menu data to sort.
 */
export function menuDataSorted(menuData) {
    menuData.sort((a, b) => {
        // Sort the items by rank. Defaults to 5 if not set.
        const a_rank = a._rank !== undefined ? a._rank : 5;
        const b_rank = b._rank !== undefined ? b._rank : 5;
        if (a_rank < b_rank) {
            return -1;
        }
        if (a_rank > b_rank) {
            return 1;
        }
        // If ranks are equal, sort by text.
        const a_text = a.text;
        const b_text = b.text;
        if (a_text < b_text) {
            return -1;
        }
        if (a_text > b_text) {
            return 1;
        }
        return 0;
    });
}
